import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CopyIcon from '@mui/icons-material/FileCopy'
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControlLabel, IconButton, List, ListItem, ListItemText, Paper, Typography } from '@mui/material'
import { DataGrid, GridColumnVisibilityModel, GridFilterModel, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { HardwareType, MarkupPropsType, MarkupType, MaterialGroupType, MaterialType } from '../../api/appAPI'
import { HardwareProps } from '../../classes/Hardware'
import { saveGridStateInLocalStorage } from '../../common/helpers'
import { addHardware, addMarkup, copyHardware, deleteHardware, editHardware } from '../../redux/itemReducer'
import { StateType } from '../../redux/store'
import { EnqueueSnackbarType } from '../common/Helpers'
import { actions as appActions } from './../../redux/appReducer'
import DefineHardware from './DefineHardware'
import EditHardware from './EditHardware'

interface IProps {
    hardwaresList: HardwareType[]
    markups: MarkupType[]
    materials: MaterialType[]
    materialGroups: MaterialGroupType[]
    hardwareGridVisibilityModel: GridColumnVisibilityModel | undefined
    hardwareGridSortModel: GridSortModel | undefined
    hardwareGridFilterModel: GridFilterModel | undefined
    activeHardwareId: number

    addHardware(hardwareProps: HardwareProps, enqueueSnackbar: EnqueueSnackbarType): void
    editHardware(hardware: HardwareType, enqueueSnackbar: EnqueueSnackbarType): void
    deleteHardware(id: number, enqueueSnackbar: EnqueueSnackbarType): void
    copyHardware(id: number, enqueueSnackbar: EnqueueSnackbarType): void
    addMarkup(markup: MarkupPropsType, enqueueSnackbar: EnqueueSnackbarType): void
    setHardwareGridVisibilityModel(model: GridColumnVisibilityModel | undefined): void
    setHardwareGridSortModel(model: GridSortModel | undefined): void
    setHardwareGridFilterModel(model: GridFilterModel | undefined): void
    setActiveHardwareId(value: number): void
    openMaterialDetails(id: number): void
    openMaterialGroupDetails(id: number): void
}

const HardwareTab: FC<IProps> = props => {
    const [activeHardware, setActiveHardware] = useState<undefined | HardwareType>(props.hardwaresList.find(h => h.id === props.activeHardwareId))
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [deleteID, setDeleteID] = useState<null | number>(null)
    const [ifSureCheckbox, setIfSureCheckbox] = useState(false)

    const { enqueueSnackbar } = useSnackbar()

    const handleCopyHardwareClick = (id: number) => {
        props.copyHardware(id, enqueueSnackbar)
    }

    const handleDeleteHardwareClick = (id: number) => {
        setDeleteID(id)
        setDeleteDialogOpen(true)
    }

    const handleDeleteDialogClose = () => {
        setDeleteID(null)
        setDeleteDialogOpen(false)
    }

    const handleDeleteDialogDeleteClick = () => {
        if (deleteID)
            props.deleteHardware(deleteID, enqueueSnackbar)
        handleDeleteDialogClose()
    }

    useEffect(() => {
        setActiveHardware(props.hardwaresList.find(h => h.id === props.activeHardwareId))
    }, [props.activeHardwareId])

    useEffect(() => {
        saveGridStateInLocalStorage({ visibilityModel: props.hardwareGridVisibilityModel, filterModel: props.hardwareGridFilterModel, sortModel: props.hardwareGridSortModel }, 'hardware')
    }, [props.hardwareGridVisibilityModel, props.hardwareGridFilterModel, props.hardwareGridSortModel])

    return (
        <Box
            display='flex'
            style={{ height: window.innerHeight - 48 }}
        >
            <Box
                display='flex'
                flexDirection='column'
                alignItems='center'
                pt={2}
                style={{ borderRight: 'solid 1px black', width: '50%' }}
            >
                <Typography variant='h6'>Parts List</Typography>

                <Paper style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
                    <List component="nav" style={{ padding: 0 }}>
                        <ListItem button onClick={() => { props.setActiveHardwareId(0) }}>
                            <ListItemText primary='New Part' />
                            <AddIcon />
                        </ListItem>
                        <Divider />
                        {/* {
                            props.hardwaresList.map(h => <Fragment key={h.name}>
                                <ListItem button onClick={() => { setActiveHardware(h) }}>
                                    <ListItemText primary={h.name} />
                                    <ListItemSecondaryAction>
                                        <IconButton edge="end" onClick={() => { handleCopyHardwareClick(h.id) }}>
                                            <CopyIcon />
                                        </IconButton>
                                        <IconButton edge="end" onClick={() => { handleDeleteHardwareClick(h.id) }}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                                <Divider />
                            </Fragment>)
                        } */}
                    </List>
                </Paper>
                <DataGrid
                    style={{ width: '90%', maxHeight: '90%', overflowY: 'auto' }}
                    columns={[
                        { field: 'id', },
                        { field: 'date', headerName: 'Last Modified', type: 'datetime', minWidth: 160 },
                        { field: 'name', headerName: 'Name', minWidth: 200 },
                        { field: 'type', headerName: 'Type', width: 150 },
                        { field: 'description', headerName: 'Description', width: 200, },
                        { field: 'unitPrice', headerName: 'Unit Price', width: 110 },
                        { field: 'markup', headerName: 'Markup', width: 110, },
                        { field: 'materialBased', headerName: 'Material Based', width: 120 },
                        { field: 'materialIds', headerName: 'Material Ids', width: 200, },
                        { field: 'defaultMaterialId', headerName: 'Default Material Id', width: 120, },
                        { field: 'children', headerName: 'Children Ids', width: 120, },
                        {
                            field: 'actions',
                            headerName: 'Actions',
                            filterable: false,
                            sortable: false,
                            renderCell: (params: GridRenderCellParams) => <Box onClick={(e) => {
                                e.stopPropagation() // don't select this row after clicking 
                            }}>
                                <IconButton edge="end" onClick={() => { handleCopyHardwareClick(parseInt(params.id.toString())) }}>
                                    <CopyIcon />
                                </IconButton>
                                {props.hardwaresList.find(h => h.id === parseInt(params.id.toString()))?.editable === '1' &&
                                    <IconButton edge="end" onClick={() => { handleDeleteHardwareClick(parseInt(params.id.toString())) }}>
                                        <DeleteIcon />
                                    </IconButton>
                                }
                            </Box>,
                            width: 150,
                        },
                    ]}

                    // hideFooterPagination
                    rows={props.hardwaresList.map(hardware => ({
                        id: hardware.id,
                        date: new Date(hardware.date),
                        name: hardware.name,
                        type: hardware.type,
                        description: hardware.description,
                        unitPrice: hardware.unitPrice,
                        markup: props.markups.find(m => m.id === hardware.markupId)?.name || hardware.markupId,
                        materialBased: hardware.materialsIds.length > 0 ? '✓' : '',
                        materialIds: hardware.materialsIds.join(','),
                        defaultMaterialId: hardware.defaultMaterialId,
                        children: hardware.childrenIds.join(','),
                    }))}
                    onRowClick={(param) => {
                        props.setActiveHardwareId(parseInt(param.id.toString()))
                    }}

                    columnVisibilityModel={props.hardwareGridVisibilityModel}
                    onColumnVisibilityModelChange={(model) => {
                        props.setHardwareGridVisibilityModel(model)
                    }}

                    sortModel={props.hardwareGridSortModel}
                    onSortModelChange={(model) => {
                        if (JSON.stringify(model) !== JSON.stringify(props.hardwareGridSortModel))
                            props.setHardwareGridSortModel(model)
                    }}

                    filterModel={props.hardwareGridFilterModel}
                    onFilterModelChange={(model) => {
                        if (JSON.stringify(model) !== JSON.stringify(props.hardwareGridFilterModel))
                            props.setHardwareGridFilterModel(model)
                    }}

                    // onStateChange={(state) => {
                    //     for (const columnName in state.columns.lookup) {
                    //         const column = state.columns.lookup[columnName]
                    //         const stateColumn = props.hardwareGridVisibilityModel.find(c => c.field === columnName)

                    //         if (stateColumn) {
                    //             if (column.hide !== stateColumn.hide) {
                    //                 props.setHardwareGridVisibilityModel(columnName, column.hide || false)
                    //             }
                    //         }
                    //     }
                    // }}

                    showCellVerticalBorder
                    rowSelectionModel={props.activeHardwareId}
                />
            </Box>
            {activeHardware === undefined
                ? <DefineHardware markups={props.markups} hardwareList={props.hardwaresList} materials={props.materials} materialGroups={props.materialGroups} addMarkup={props.addMarkup} addHardware={props.addHardware} openMaterialDetails={props.openMaterialDetails} openMaterialGroupDetails={props.openMaterialGroupDetails} />
                : <EditHardware markups={props.markups} hardwareList={props.hardwaresList} materials={props.materials} materialGroups={props.materialGroups} addMarkup={props.addMarkup} hardware={activeHardware} editHardware={props.editHardware} openMaterialDetails={props.openMaterialDetails} openMaterialGroupDetails={props.openMaterialGroupDetails} />
            }

            <Dialog
                open={deleteDialogOpen}
            >
                <DialogTitle>Delete Part</DialogTitle>
                <DialogContent dividers>
                    <Typography>
                        Are you sure you want to delete <b>"{
                            props.hardwaresList.find(hardware => hardware.id === deleteID)?.name
                        }"</b> part?
                    </Typography>
                    <br />
                    {
                        props.hardwaresList.filter(h => h.childrenIds.includes(deleteID ? deleteID : 0)).length > 0
                        && <>
                            <Typography>Part is child of</Typography>
                            {
                                props.hardwaresList
                                    .filter(h => h.childrenIds.includes(deleteID ? deleteID : 0))
                                    .map(h => <Typography key={h.id} component='li'>{h.name}</Typography>)
                            }
                            <br />
                            <FormControlLabel
                                control={<Checkbox checked={ifSureCheckbox} onChange={(e) => { setIfSureCheckbox(e.target.checked) }} />}
                                label="Yes, I'm sure!"
                            />
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteDialogClose} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteDialogDeleteClick}
                        color="secondary"
                        disabled={props.hardwaresList.filter(h => h.childrenIds.includes(deleteID ? deleteID : 0)).length > 0 ? !ifSureCheckbox : false}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

const mapStateToProps = (state: StateType) => ({
    hardwaresList: state.itemReducer.hardwaresList,
    markups: state.itemReducer.markups,
    materials: state.itemReducer.materials,
    materialGroups: state.itemReducer.materialGroups,
    hardwareGridVisibilityModel: state.appReducer.hardwareGrid.visibilityModel,
    hardwareGridSortModel: state.appReducer.hardwareGrid.sortModel,
    hardwareGridFilterModel: state.appReducer.hardwareGrid.filterModel,
    activeHardwareId: state.appReducer.activeHardwareId,
})

export default connect(mapStateToProps, {
    addHardware,
    editHardware,
    deleteHardware,
    copyHardware,
    addMarkup,
    setHardwareGridVisibilityModel: appActions.setHardwareGridVisibilityModel,
    setHardwareGridSortModel: appActions.setHardwareGridSortModel,
    setHardwareGridFilterModel: appActions.setHardwareGridFilterModel,
    setActiveHardwareId: appActions.setActiveHardwareId,
    openMaterialDetails: appActions.openMaterialDetails,
    openMaterialGroupDetails: appActions.openMaterialGroupDetails,
})(HardwareTab)